/* eslint complexity: 0 */
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {SVGIcon} from '@homeaway/react-svg';
import {CHEVRON_DOWN__16} from '@homeaway/svg-defs';
import classNames from 'classnames';
import './NavigationBase.less';

/**
 * Navigation Base
 *
 */
class NavigationBase extends Component {
    static propTypes = {
        /** Array of link objects. They could include the properties: label, href, disabled. Use the node property to pass in a Link component from react-router. */
        links: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.shape({
                className: PropTypes.string,
                disabled: PropTypes.bool,
                href: PropTypes.string.isRequired,
                label: PropTypes.node.isRequired,
                ref: PropTypes.object
            })),
            PropTypes.arrayOf(PropTypes.shape({
                className: PropTypes.string,
                disabled: PropTypes.bool,
                href: PropTypes.string,
                label: PropTypes.node,
                node: PropTypes.node.isRequired,
                ref: PropTypes.object
            }))
        ]).isRequired,
        /** Inverse styling */
        inverse: PropTypes.bool,
        /**
         * Event handler trigger when a user clicks a link, will receive both
         * the event and active index number as params. Update activeIndex in
         * this handler to the specified index to transition the navigation.
         */
        onLinkClick: PropTypes.func,
        /** The index number of active nav item */
        activeIndex: PropTypes.number,
        /** Force tabs to be used in mobile, instead of a dropdown select. Defaults true on VerticalNavigaiton */
        noMobileSelect: PropTypes.bool,
    };

    static defaultProps = {
        activeIndex: 0,
        onLinkClick: () => {},
        noMobileSelect: false
    };

    /* istanbul ignore next */
    constructor(props) {
        super(props);

        this.state = {
            isTransitioning: false
        };
    }

    componentDidMount() {
        // Silly check for Enzyme 3 shallow rendering now having refs
        if (this.ul) {
            this.updateHighlight();
        }

        /* istanbul ignore next */
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.updateHighlight);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.activeIndex !== nextProps.activeIndex || this.props.links !== nextProps.links) {
            // When the activeIndex or the links are updated, transition
            // the highlight to the new item.
            this.updateHighlight();
        }
    }

    /* istanbul ignore next */
    componentWillUnmount() {
        /* istanbul ignore next */
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.updateHighlight);
        }

        if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }
    }

    updateHighlight = () => {
        this.setState({
            isTransitioning: true
        });

        this.transitionTimeout = setTimeout(() => {
            this.setState({
                isTransitioning: false
            });
        }, 300);
    }

    handleClick = (isNode, e) => {
        let index;

        if (e.currentTarget.dataset.index) {
            index = Number(e.currentTarget.dataset.index);
        } else {
            index = e.currentTarget.selectedIndex;
        }

        // Assume the node being used is a <Link> with an href to route to.
        if (!isNode) {
            e.preventDefault();
        }
        this.props.onLinkClick(e, index);
    }

    handleChange = (e) => {
        this.handleClick(false, e);
    }

    renderLink = (link, i) => {
        const {activeIndex} = this.props;
        const isActive = (Number(activeIndex) === i);
        const aClasses = classNames(
            {
                'Navigation__a': true,
                'Navigation__a--active': isActive,
                'Navigation__a--disabled': link.disabled
            }, link.className);
        const aProps = {
            ['data-index']: i,
            className: aClasses,
            ...((!link.node && !link.disabled) && {href: link.href}),
            ...(!link.disabled && {onClick: this.handleClick.bind(this, !!link.node)})
        };

        /* For the most part, this is assuming link.node is a Link component from react-router-dom.
         * Other use cases' success may vary.  (Note that we don't pass href in link.node as Link uses `to` prop).
         * Need to wrap the cloned link.node in a <span> so the ref is a DOM element and its
         * dimensions can later be calculated.
         */
        if (link.node) {
            return (
                <span ref={(element) => {
                    link.ref = element;
                }}
                >
                    {React.cloneElement(
                        link.node, {
                            ...aProps
                        }
                    )}
                </span>
            );
        }

        return (
            <a {...aProps} ref={(element) => {
                link.ref = element;
            }}
            >
                {link.label}
            </a>
        );
    }

    /* eslint-disable complexity */
    render() {
        const nav = this;
        const {links, className, activeIndex, leftAlign, inverse, noMobileSelect, vertical, withBorder} = this.props;
        const {isTransitioning} = this.state;
        const navClasses = classNames(className,
            {
                'Navigation': !inverse,
                'Navigation--inverse': inverse,
                'Navigation--left-align': leftAlign,
                'Navigation--mobile-tabs': noMobileSelect && !vertical,
                'Navigation--divider': withBorder,
            },
            className
        );

        let highlightStyle;
        if (this.ul && this.ul.childNodes && this.ul.childNodes.length > 0) {
            const activeBarWidth = vertical ? 24 : this.ul.childNodes[activeIndex].offsetWidth;
            const activeBarOffset = vertical ? this.ul.childNodes[activeIndex].offsetTop + this.ul.childNodes[activeIndex].childNodes[0].offsetTop : this.ul.childNodes[activeIndex].offsetLeft;
            const transformString = `translate${vertical ? 'Y' : 'X'}(${activeBarOffset}px) scale${vertical ? 'X' : 'Y'}(${isTransitioning ? '2' : '1'})`;
            highlightStyle = {
                transform: transformString,
                width: !vertical ? activeBarWidth : '',
                height: vertical ? 24 : ''
            };
        }

        const iconVariant = inverse ? 'text-white' : '';

        return (
            <nav className={navClasses}>
                <ul
                    className="Navigation__ul"
                    ref={
                        /* istanbul ignore next */
                        (ul) => {
                            this.ul = ul;
                        }
                    }
                >
                    {links.map((link, i) => {
                        return (
                            <li key={`nav-item-${i}`} className="Navigation__li">
                                {this.renderLink(link, i)}
                            </li>
                        );
                    })}
                    <li aria-hidden="true" style={highlightStyle} className="Navigation__li--highlight" />
                </ul>
                {!noMobileSelect &&
                    <div className="Navigation__select-wrapper visible-xs">
                        <select value={links[activeIndex].href} className="Navigation__select" onChange={this.handleChange}>
                            {links.map((link, i) => {
                                return (
                                    <option disabled={link.disabled} value={link.href} key={`nav-option-${i}`}>
                                        {link.label}
                                    </option>
                                );
                            })}
                        </select>
                        <SVGIcon className={`Navigation__select-chevron ${iconVariant}`} markup={CHEVRON_DOWN__16}/>
                    </div>
                }
            </nav>
        );
    }
    /* eslint-enable */
}

export default NavigationBase;
